import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { formatPhoneDisplay } from '../utils/phoneFormat';

// Super admin: every company with a SnowTrack texting number or a provisioning
// request (SMS Phase 6a), from GET /api/admin/sms/numbers. Reconcile reads
// Twilio and lists orphan sub-accounts, each with Claim (when the friendly
// name names a company that is still waiting) or Release orphan (typed last
// four). Per-company Provision now and Release live in AdminSmsPanel.
// SMS Phase 6b adds the brand and campaign status as last polled, the campaign
// fee in the monthly cost, and Refresh status (one poll now) on each row that
// is waiting on the carriers.
// SMS Phase 6c adds, on each row, why it stopped (failureReason), when the
// platform owner was last emailed about it, and Retry for a brand or campaign
// the carriers rejected: the first click shows what Twilio charges again, the
// second resubmits (POST /api/admin/companies/:id/sms/retry).
const API = `${process.env.REACT_APP_API_URL}/api/admin/sms`;

const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

// What Twilio charges again for a Retry, in words.
export const retryFeeText = (retry) => (retry.twilioFeeCents > 0
  ? `Twilio charges the ${retry.what} fee again: ${dollars(retry.twilioFeeCents)}.`
  : `No new Twilio fee for a ${retry.what} resubmission.`);

const shortDate = (value) => new Date(value).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

// The steps the registration poll reads (server smsProvisioning.POLLED_STEPS).
export const WAITING_STEPS = ['brand_submitted', 'brand_otp_pending', 'campaign_submitted'];

function AdminSmsNumbers({ refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState({});
  // The company whose Retry is waiting for its second click.
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async (reconcile) => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.get(`${API}/numbers${reconcile ? '?reconcile=1' : ''}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load texting numbers');
    } finally {
      setBusy(false);
    }
  }, []);

  // refreshKey: AdminPanel bumps it on pull-to-refresh.
  useEffect(() => { load(false); }, [load, refreshKey]);

  const orphanAction = async (orphan, action) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const body = action === 'release' ? { confirmLast4: confirm[orphan.ref] || '' } : {};
      await axios.post(`${API}/orphans/${orphan.ref}/${action}`, body);
      setNotice(action === 'claim'
        ? `Claimed sub-account AC...${orphan.subaccountLast4} for ${orphan.companyName || `company ${orphan.companyId}`}.`
        : `Released sub-account AC...${orphan.subaccountLast4}.`);
      setConfirm({ ...confirm, [orphan.ref]: '' });
      await load(true);
    } catch (err) {
      setError(err.response?.data?.error || `Could not ${action} the orphan`);
      setBusy(false);
    }
  };

  const refresh = async (n) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/companies/${n.companyId}/sms/refresh`);
      const r = res.data;
      setNotice(`${n.companyName}: ${r.changed ? 'now' : 'still'} at ${r.step}`
        + ` (brand ${r.brandStatus || '-'}, campaign ${r.campaignStatus || '-'})${r.error ? `, ${r.error}` : ''}.`);
      await load(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not refresh the status');
      setBusy(false);
    }
  };

  const retry = async (n) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/companies/${n.companyId}/sms/retry`);
      const r = res.data;
      setNotice(`${n.companyName}: ${r.what} resubmitted, now at ${r.step}`
        + ` (brand ${r.brandStatus || '-'}, campaign ${r.campaignStatus || '-'}).`);
      setRetrying(null);
      await load(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resubmit');
      setRetrying(null);
      setBusy(false);
    }
  };

  const rec = data && data.reconcile;

  return (
    <div className="companies-section">
      <h2>Texting numbers</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      {!data && !error && <p className="text-muted">Loading...</p>}
      {data && (
        <>
          <p className="text-muted">
            Provisioning: {data.provisioning}
            {!data.masterConfigured && ' · master Twilio credentials are not configured'}
          </p>
          <button type="button" className="btn btn-secondary" onClick={() => load(true)} disabled={busy}>
            {busy ? 'Working...' : 'Reconcile with Twilio'}
          </button>
          <div className="table-container" style={{ marginTop: '12px' }}>
            <table className="companies-table">
              <thead>
                <tr>
                  <th>Company</th><th>Number</th><th>Sub-account</th><th>Service</th>
                  <th>Step</th><th>Brand</th><th>Campaign</th><th>Setup fee</th><th>Twilio / month</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.numbers.length === 0 && (
                  <tr><td colSpan="10" className="text-muted">No provisioned numbers or requests.</td></tr>
                )}
                {data.numbers.map((n) => (
                  <tr key={n.companyId}>
                    <td>{n.companyName} ({n.companyId})</td>
                    <td>{n.phoneNumber ? formatPhoneDisplay(n.phoneNumber) : '-'}</td>
                    <td>{n.kind === 'manual' ? `manual (company ${n.companyId})` : `AC...${n.subaccountLast4}`}</td>
                    <td>{n.serviceLast4 ? `MG...${n.serviceLast4}` : '-'}</td>
                    <td>
                      {n.releasedAt ? 'released' : (n.step || '-')}
                      {n.failureReason && <div style={{ fontSize: '0.8rem' }}>{n.failureReason}</div>}
                      {n.error && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{n.error} ({n.attempts} attempts)</div>}
                      {n.error && n.lastAlertAt && (
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>Emailed to you {shortDate(n.lastAlertAt)}</div>
                      )}
                    </td>
                    <td>{n.brandStatus || '-'}</td>
                    <td>{n.campaignStatus || '-'}</td>
                    <td>
                      {n.feePaid ? 'paid' : 'not paid'}
                      {n.reinstatedAt && n.twilioFeesAgainCents > 0 && (
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          Reinstated: Twilio charges {dollars(n.twilioFeesAgainCents)} again to register
                        </div>
                      )}
                    </td>
                    <td>{dollars(n.monthlyTwilioCents)}</td>
                    <td>
                      {!n.releasedAt && WAITING_STEPS.includes(n.step) && (
                        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => refresh(n)}>
                          Refresh status
                        </button>
                      )}
                      {!n.releasedAt && n.retry && retrying !== n.companyId && (
                        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setRetrying(n.companyId)}>
                          Retry {n.retry.what}
                        </button>
                      )}
                      {!n.releasedAt && n.retry && retrying === n.companyId && (
                        <div style={{ marginTop: '4px' }}>
                          <div style={{ fontSize: '0.8rem' }}>{retryFeeText(n.retry)}</div>
                          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => retry(n)}>
                            Confirm retry
                          </button>
                          {' '}
                          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setRetrying(null)}>
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rec && !rec.ok && <div className="alert alert-error">Reconcile could not read Twilio: {rec.reason}</div>}
          {rec && rec.ok && (
            <>
              <h3 style={{ marginTop: '16px' }}>Orphans ({rec.orphans.length} of {rec.checked} sub-accounts checked)</h3>
              {rec.busy.length > 0 && <p className="text-muted">Skipped while provisioning: companies {rec.busy.join(', ')}</p>}
              {rec.orphans.length === 0 && <p className="text-muted">Every SnowTrack sub-account is claimed by its row.</p>}
              {rec.orphans.map((o) => (
                <div key={o.ref} className="form-group" style={{ borderTop: '1px solid var(--border-color, #e0e0e0)', paddingTop: '8px' }}>
                  <p style={{ margin: 0 }}>
                    AC...{o.subaccountLast4} &middot; {o.companyName || 'no live company'} ({o.companyId}) &middot; {o.reason}
                    {o.extraNumbers > 0 && ` · ${o.extraNumbers} extra numbers`}
                  </p>
                  {o.claimable ? (
                    <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => orphanAction(o, 'claim')}>
                      Claim
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                      <input className="form-input" style={{ maxWidth: '10rem' }} placeholder="Last four"
                        aria-label={`Type ${o.subaccountLast4} to release`} value={confirm[o.ref] || ''}
                        onChange={(e) => setConfirm({ ...confirm, [o.ref]: e.target.value })} autoComplete="off" />
                      <button type="button" className="btn btn-danger" disabled={busy || !(confirm[o.ref] || '').trim()}
                        onClick={() => orphanAction(o, 'release')}>
                        Release orphan
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AdminSmsNumbers;
